import { Injectable } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { AdminLifecycleManagerService } from './admin-lifecycle-manager.service';
import { DigitalTwinLifecycleManagerService } from './digital-twin-lifecycle-manager.service';
import { MapLifecycleManagerService } from './map-lifecycle-manager.service';

/**
 * This service is responsible for managing lifecycle transitions between major areas of the application
 * (e.g., Map, Digital Twin, Admin). It ensures that when navigating between these areas:
 * - **State Resets:** States from services tied to the previous area are reset to avoid stale or inconsistent data.
 * - **Resource Cleanup:** Resources such as WebGL objects, event listeners, or other heavy resources
 *   are properly disposed of to prevent memory leaks.
 *
 * The service works in conjunction with area-specific lifecycle managers (e.g., MapLifecycleManagerService,
 * DigitalTwinLifecycleManagerService). Each manager handles the cleanup logic for its respective area. This
 * centralized coordination prevents the need for services directly managing one another, reducing coupling
 * and maintaining modularity.
 *
 * ### Long-Term Goal
 * In the long term, the aim is to refactor most services to be scoped appropriately to their respective
 * modules or components. This would allow Angular's built-in lifecycle management to handle the cleanup
 * process automatically. However, even after refactoring, this service and the lifecycle managers will
 * continue to serve an important role:
 * - They will handle global services that require manual cleanup during area transitions.
 * - They will act as a central mechanism to ensure cross-area transitions are always clean and predictable.
 *
 * By adopting this approach, the application minimizes the risk of stale data, memory leaks, and unnecessary
 * resource consumption, ensuring a more stable and maintainable codebase.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationLifecycleCoordinatorService {
  private currentArea: string | null = null;

  constructor(
    private router: Router,
    private mapManager: MapLifecycleManagerService,
    private digitalTwinManager: DigitalTwinLifecycleManagerService,
    private adminManager: AdminLifecycleManagerService
  ) {
    const initialUrl = this.router.url;
    this.currentArea = this.getAreaFromUrl(initialUrl);

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.handleNavigation(event.url);
      }
    });
  }

  private handleNavigation(url: string) {
    const newArea = this.getAreaFromUrl(url);

    if (newArea !== this.currentArea) {
      this.handleAreaTransition();
      this.currentArea = newArea; // Update the current area
    }
  }

  private handleAreaTransition() {
    if (this.currentArea === 'map') {
      this.mapManager.resetAll();
    } else if (this.currentArea === 'digitaltwin') {
      this.digitalTwinManager.cleanExitArea();
    } else if (this.currentArea === 'admin') {
      this.adminManager.resetAll();
    }

    // Additional logic can be added here if needed for newArea
  }

  private getAreaFromUrl(url: string): string {
    if (url.startsWith('/map')) {
      return 'map';
    }
    if (url.startsWith('/digitaltwin')) {
      return 'digitaltwin';
    }
    if (url.startsWith('/admin')) {
      return 'admin';
    }
    return 'unknown';
  }
}

